import { getUserInfo } from '@utils/Util';

import { PERMISSIONS } from '@/constants/constant';

// Same keys as the CRM's own toolbar: both apps share an origin in production,
// so the toolbar position and the calendar "seen" state carry across.
const POSITION_KEY = 'nitex:quick-access:position';
const SEEN_EVENTS_KEY_PREFIX = 'nitex:quick-access:seen-events:';
const DOCK_KEY = 'nitex:quick-access:dock';

// The toolbar is always docked to the left or right edge; `bottom` is its offset along that edge.
export const DEFAULT_TOOLBAR_POSITION = { side: 'right', bottom: 96 };

export const readToolbarPosition = () => {
	try {
		const parsed = JSON.parse(localStorage.getItem(POSITION_KEY) ?? 'null');
		if (parsed && Number.isFinite(parsed.bottom)) {
			return { side: parsed.side === 'left' ? 'left' : 'right', bottom: parsed.bottom };
		}
	} catch {
		// Fall back to the default spot.
	}
	return DEFAULT_TOOLBAR_POSITION;
};

export const writeToolbarPosition = (position) => {
	try {
		if (position) localStorage.setItem(POSITION_KEY, JSON.stringify(position));
		else localStorage.removeItem(POSITION_KEY);
	} catch {
		// Remembering the position is a convenience only.
	}
};

/**
 * Whether drawers open pinned, and which one ('mail' | 'calendar') is open while
 * pinned, so a pinned drawer survives reloads and the jump into the CRM.
 */
export const readDockState = () => {
	try {
		const parsed = JSON.parse(localStorage.getItem(DOCK_KEY) ?? 'null');
		if (parsed && typeof parsed === 'object') {
			const pinned = parsed.pinned === true;
			const panel = parsed.panel === 'mail' || parsed.panel === 'calendar' ? parsed.panel : null;
			return { pinned, panel: pinned ? panel : null };
		}
	} catch {
		// Unpinned by default.
	}
	return { pinned: false, panel: null };
};

export const writeDockState = (state) => {
	try {
		localStorage.setItem(DOCK_KEY, JSON.stringify(state));
	} catch {
		// Pinning still works for this page view without storage.
	}
};

const seenEventsKey = () => `${SEEN_EVENTS_KEY_PREFIX}${getUserInfo()?.id ?? 'anonymous'}`;

/** Event id -> epoch ms after which the entry can be forgotten (the event is over). */
export const readSeenEvents = () => {
	try {
		const parsed = JSON.parse(localStorage.getItem(seenEventsKey()) ?? '{}');
		return parsed && typeof parsed === 'object' ? parsed : {};
	} catch {
		return {};
	}
};

export const markEventsSeen = (entries) => {
	const now = Date.now();
	const next = {};
	Object.entries(readSeenEvents()).forEach(([id, expiresAt]) => {
		if (expiresAt > now) next[id] = expiresAt;
	});
	entries.forEach((entry) => {
		next[entry.id] = entry.expiresAt;
	});
	try {
		localStorage.setItem(seenEventsKey(), JSON.stringify(next));
	} catch {
		// Without storage the badge simply counts every upcoming event.
	}
	return next;
};

/** The CRM menu can sit in either the main or the "more" menu. */
export const hasCrmAccess = () => {
	const userInfo = getUserInfo();
	return [...(userInfo?.mainMenus || []), ...(userInfo?.moreMenus || [])].includes(PERMISSIONS.MENU_CRM);
};
